import { useCallback, useState } from 'react';

export function useRemoteDeleteFlow({
  interactionLocked,
  isUploadBlockedByCaseStatus,
  waitForNextPaint,
  waitForMilliseconds,
  deleteDialogMinVisibleMs,
  handleDeleteRemoteCaseFile,
  handleDeleteRemoteTranscriptFile
}) {
  const [pendingRemoteDeleteItem, setPendingRemoteDeleteItem] = useState(null);
  const [pendingRemoteTranscriptDeleteItem, setPendingRemoteTranscriptDeleteItem] = useState(null);
  const [remoteDeleteInProgress, setRemoteDeleteInProgress] = useState(false);
  const [remoteTranscriptDeleteInProgress, setRemoteTranscriptDeleteInProgress] = useState(false);
  const [remoteDeletePhase, setRemoteDeletePhase] = useState('');
  const [remoteDeleteError, setRemoteDeleteError] = useState('');

  const closeRemoteDeleteDialog = useCallback(() => {
    if (remoteDeleteInProgress) {
      return;
    }

    setPendingRemoteDeleteItem(null);
  }, [remoteDeleteInProgress]);

  const closeRemoteTranscriptDeleteDialog = useCallback(() => {
    if (remoteTranscriptDeleteInProgress) {
      return;
    }

    setPendingRemoteTranscriptDeleteItem(null);
  }, [remoteTranscriptDeleteInProgress]);

  const openRemoteDeleteDialog = useCallback((item) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      return;
    }

    setRemoteDeleteError('');
    setRemoteDeletePhase('');
    setPendingRemoteDeleteItem(item);
  }, [interactionLocked, isUploadBlockedByCaseStatus]);

  const openRemoteTranscriptDeleteDialog = useCallback((item) => {
    if (interactionLocked || isUploadBlockedByCaseStatus) {
      return;
    }

    setRemoteDeleteError('');
    setRemoteDeletePhase('');
    setPendingRemoteTranscriptDeleteItem(item);
  }, [interactionLocked, isUploadBlockedByCaseStatus]);

  const confirmDeleteRemoteCaseFile = useCallback(async () => {
    if (interactionLocked || isUploadBlockedByCaseStatus || !pendingRemoteDeleteItem || remoteDeleteInProgress) {
      return;
    }

    const itemToDelete = pendingRemoteDeleteItem;
    const startedAt = Date.now();

    try {
      setRemoteDeleteInProgress(true);
      setRemoteDeletePhase('deleting');
      setRemoteDeleteError('');

      await waitForNextPaint();

      const deleted = await handleDeleteRemoteCaseFile(itemToDelete);
      if (deleted) {
        const remainingMs = deleteDialogMinVisibleMs - (Date.now() - startedAt);
        if (remainingMs > 0) {
          await waitForMilliseconds(remainingMs);
        }

        await waitForNextPaint();
        setPendingRemoteDeleteItem(null);
        setRemoteDeletePhase('');
      }
    } finally {
      setRemoteDeleteInProgress(false);
    }
  }, [
    deleteDialogMinVisibleMs,
    handleDeleteRemoteCaseFile,
    interactionLocked,
    isUploadBlockedByCaseStatus,
    pendingRemoteDeleteItem,
    remoteDeleteInProgress,
    waitForMilliseconds,
    waitForNextPaint
  ]);

  const confirmDeleteRemoteTranscriptFile = useCallback(async () => {
    if (
      interactionLocked ||
      isUploadBlockedByCaseStatus ||
      !pendingRemoteTranscriptDeleteItem ||
      remoteTranscriptDeleteInProgress
    ) {
      return;
    }

    const itemToDelete = pendingRemoteTranscriptDeleteItem;
    const startedAt = Date.now();

    try {
      setRemoteTranscriptDeleteInProgress(true);
      setRemoteDeletePhase('deleting');
      setRemoteDeleteError('');

      await waitForNextPaint();

      const deleted = await handleDeleteRemoteTranscriptFile(itemToDelete);
      if (deleted) {
        const remainingMs = deleteDialogMinVisibleMs - (Date.now() - startedAt);
        if (remainingMs > 0) {
          await waitForMilliseconds(remainingMs);
        }

        await waitForNextPaint();
        setPendingRemoteTranscriptDeleteItem(null);
        setRemoteDeletePhase('');
      }
    } finally {
      setRemoteTranscriptDeleteInProgress(false);
    }
  }, [
    deleteDialogMinVisibleMs,
    handleDeleteRemoteTranscriptFile,
    interactionLocked,
    isUploadBlockedByCaseStatus,
    pendingRemoteTranscriptDeleteItem,
    remoteTranscriptDeleteInProgress,
    waitForMilliseconds,
    waitForNextPaint
  ]);

  return {
    pendingRemoteDeleteItem,
    pendingRemoteTranscriptDeleteItem,
    remoteDeleteInProgress,
    remoteTranscriptDeleteInProgress,
    remoteDeletePhase,
    remoteDeleteError,
    setRemoteDeletePhase,
    setRemoteDeleteError,
    openRemoteDeleteDialog,
    openRemoteTranscriptDeleteDialog,
    closeRemoteDeleteDialog,
    closeRemoteTranscriptDeleteDialog,
    confirmDeleteRemoteCaseFile,
    confirmDeleteRemoteTranscriptFile
  };
}
